import { State } from '@progress/kendo-data-query';
import { useState, useEffect, FC, useRef, useTransition, memo, useMemo } from 'react';
import { Customer } from 'src/types/customer';
import customerApi from 'src/api/ActiveCustomerApi';
import useAuth from 'src/hooks/useAuth';
import { Autocomplete, TextField, Theme, Box, Checkbox, FormControlLabel, CircularProgress, SxProps } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { LOCAL_STORAGE_KEYS } from 'src/constants';

interface CustomerSearchProps {
  defaultCustomer?: Customer;
  onCustomerSelected: (customer: Customer) => void;
  placeholder?: string;
  className?: string;
  sx?: SxProps<Theme>;
  defaultSearchAll?: boolean;
}

const MIN_FILTER_LENGTH = 2;

const CustomerSearch: FC<CustomerSearchProps> = ({ defaultCustomer, onCustomerSelected, placeholder, className, sx, defaultSearchAll = false }) => {
  //The options for the typeahead
  const [customers, setCustomers] = useState<Customer[]>([]);

  const [recentCustomers, setRecentCustomers] = useState<Customer[]>(() => {
    const recent = localStorage.getItem(LOCAL_STORAGE_KEYS.RecentlySelectedCustomers);
    return recent ? JSON.parse(recent) : [];
  });

  const [customer, setCustomer] = useState<Customer>(defaultCustomer ?? null);
  const [isShowAll, setIsShowAll] = useState(defaultSearchAll);
  const [textInputData, setTextInputData] = useState('');
  const currentSearchString = useRef<string>('');

  const [isSearching, setIsSearching] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPending, startTransition] = useTransition();

  const { user } = useAuth();

  const [inputData, setInputData] = useState<any>(customers);

  const SalesRepId: string = user?.salesReps && user?.salesReps.length > 0 ? user.salesReps[0] : '';

  useEffect(() => {
    onFilterChange(textInputData);
  }, [isShowAll]);

  useEffect(() => {
    if (defaultCustomer) {
      setCustomer(defaultCustomer);
    }
  }, [defaultCustomer]);

  // Combine recent customers with search results
  const combinedOptions = useMemo(() => {
    if (isSearching) {
      return customers;
    }

    if (customers.length === 0) {
      return recentCustomers;
    }

    // Filter out recent customers already in the results
    const recentNotInResults = recentCustomers.filter((recent) => !customers.some((c) => c.number === recent.number));

    return [...recentNotInResults, ...customers];
  }, [customers, recentCustomers, isSearching]);

  const onFilterChange = (searchText: string) => {
    currentSearchString.current = searchText;

    if (searchText == null || searchText.length < MIN_FILTER_LENGTH) {
      setIsSearching(false);
      startTransition(() => {
        setIsLoading(false);
        setCustomers([]);
      });
      return;
    }

    setIsSearching(true);
    setIsLoading(true);

    const lowerSearchText = searchText.toLowerCase();

    customerApi
      .getCustomersMinimal({
        take: 10,
        filter: {
          logic: 'and',
          value: '',
          filters: [
            {
              filters: [
                { field: 'tolower(numberString)', operator: 'contains', value: lowerSearchText },
                { field: 'tolower(name)', operator: 'contains', value: lowerSearchText },
              ],
              logic: 'or',
            },
            {
              filters: [
                { field: 'hardGoodsRepCode', operator: isShowAll ? 'ne' : 'eq', value: isShowAll ? '' : SalesRepId },
                { field: 'lawnGardenRepCode', operator: isShowAll ? 'ne' : 'eq', value: isShowAll ? '' : SalesRepId },
                { field: 'plantRepCode', operator: isShowAll ? 'ne' : 'eq', value: isShowAll ? '' : SalesRepId },
                { field: 'carrRepCode', operator: isShowAll ? 'ne' : 'eq', value: isShowAll ? '' : SalesRepId },
              ],
              logic: 'or',
            },
          ],
        },
      } as State)
      .then((result) => {
        if (searchText === currentSearchString.current) {
          startTransition(() => {
            setIsLoading(false);
            setCustomers(result.value);
          });
        }
      })
      .catch(() => setIsLoading(false));
  };

  const handleChange = (value) => {
    setCustomer(value);
    onCustomerSelected(value);
  };

  return (
    <Autocomplete
      className={className ?? 'customer-typeahead'}
      sx={sx}
      value={customer}
      options={combinedOptions}
      filterOptions={(x) => x}
      multiple={false}
      onChange={(e, newValue) => {
        handleChange(newValue);

        if (newValue) {
          const recentlySelectedCustomers = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.RecentlySelectedCustomers) || '[]');
          const filteredRecent = recentlySelectedCustomers.filter((c) => c.number !== newValue.number);
          const updated = [{ ...newValue }, ...filteredRecent].slice(0, 5);

          localStorage.setItem(LOCAL_STORAGE_KEYS.RecentlySelectedCustomers, JSON.stringify(updated));
          setRecentCustomers(updated);
        }
      }}
      onInputChange={(e, newValue, reason) => {
        if (reason === 'input') {
          setInputData(newValue.length >= MIN_FILTER_LENGTH ? newValue : null);
          onFilterChange(newValue.length >= MIN_FILTER_LENGTH ? newValue : null);
        }
      }}
      getOptionLabel={(option: Customer) => `${option.name} (${option.number})`}
      renderOption={(p, option: Customer) => {
        const isFirstRecent = !isSearching && recentCustomers.length > 0 && option.number === recentCustomers[0]?.number;

        let addRepIcon =
          user.salesReps.includes(option.plantRepCode) ||
          user.salesReps.includes(option.hardGoodsRepCode) ||
          user.salesReps.includes(option.lawnGardenRepCode) ||
          user.salesReps.includes(option.carrRepCode);

        // Extract key from props to pass it directly
        const { key, ...restProps } = p as any & React.HTMLAttributes<HTMLLIElement>;

        return (
          <>
            <div key={option.number}>
              {isFirstRecent && (
                <Box sx={{ display: 'flex' }} className="customer-typeahead__recent-header">
                  <Box className="customer-typeahead__recent-title">Recently Selected</Box>
                  <Box
                    className="customer-typeahead__recent-clear"
                    onClick={() => {
                      localStorage.removeItem(LOCAL_STORAGE_KEYS.RecentlySelectedCustomers);
                      setRecentCustomers([]);
                    }}
                  >
                    (Clear List)
                  </Box>
                </Box>
              )}

              <Box component="li" key={key} {...restProps} id={'customer_' + option.number}>
                {`${option.name} - ${option.city}, ${option.state} (${option.number})`}
                {addRepIcon ? (
                  <>
                    <Box sx={{ flex: 1 }}></Box>{' '}
                    <Box title="Your Account">
                      <CheckCircleIcon sx={{ fontSize: 16, ml: 1 }} />
                    </Box>
                  </>
                ) : (
                  <></>
                )}
              </Box>
            </div>
          </>
        );
      }}
      renderInput={(params) => (
        <>
          <FormControlLabel
            control={
              <Checkbox
                name="allCustomers"
                defaultChecked={defaultSearchAll}
                size="small"
                onChange={(e) => {
                  setIsShowAll(e.target.checked);
                  if (typeof inputData === 'string') {
                    setTextInputData(inputData);
                  }
                }}
              />
            }
            label="Search all customers"
          />

          {(isLoading || isPending) && <CircularProgress size={10} color="inherit" />}
          <TextField
            {...params}
            name="customerTypeahead"
            label={placeholder ?? 'Search customers...'}
            margin="normal"
            // onKeyDown={(e: any) => {
            //   e.stopPropagation();
            // }}
          />
        </>
      )}
    />
  );
};

/**
 * Typeahead component for customers, which dynamically fetches customer data as you type.
 *
 * @component
 *
 *
 * @param {string} defaultCustomer - The initial value of the typeahead input.
 * @param {function} onCustomerSelected - Callback function triggered when a customer is selected.
 * @param {string} [placeholder] - Placeholder text for the typeahead input. Will default to "Search customers..." if not provided.
 * @param {string} [className='customer-typeahead'] - CSS class name for the typeahead container.
 * @param {object} [sx] - CSS styles for the typeahead container, using the SX prop from Theme UI.
 * @param {boolean} [defaultSearchAll=false] - The initial value of the "Search all customers" checkbox. Defaults to false if not provided.
 *
 */
export default memo(CustomerSearch);
